import React, { useState } from "react";
import { motion } from "framer-motion";
import styled from "styled-components";
import tw from "twin.macro";
import { css } from "styled-components/macro"; //eslint-disable-line
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { ReactComponent as ChevronDownIcon } from "feather-icons/dist/icons/chevron-down.svg";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-7.svg";
import { ReactComponent as SvgDecoratorBlob2 } from "images/svg-decorator-blob-8.svg";

const Subheading = tw(SubheadingBase)`mb-4 text-center`;
const Heading = tw(SectionHeading)`w-full`;
const Description = tw(SectionDescription)`w-full text-center m-auto`;

const Column = tw.div`flex flex-col items-center`;
const HeaderContent = tw.div``;

const FAQSContainer = tw.dl`mt-12 max-w-4xl relative`;
const FAQ = tw.div`cursor-pointer select-none mt-5 px-8 sm:px-10 py-5 sm:py-4 rounded-lg text-gray-800 hover:text-gray-900 bg-gray-200 hover:bg-gray-300 transition duration-300`;
const Question = tw.dt`flex justify-between items-center`;
const QuestionText = tw.span`text-lg lg:text-xl font-semibold`;
const QuestionToggleIcon = motion(styled.span`
  ${tw`ml-2 transition duration-300`}
  svg {
    ${tw`w-6 h-6`}
  }
`);
const Answer = motion(tw.dd`pointer-events-none text-sm sm:text-base leading-relaxed`);

const DecoratorBlob1 = styled(SvgDecoratorBlob1)`
  ${tw`pointer-events-none -z-20 absolute right-0 top-0 h-56 w-56 opacity-15 transform translate-x-2/3 -translate-y-12 text-teal-400`}
`;
const DecoratorBlob2 = styled(SvgDecoratorBlob2)`
  ${tw`pointer-events-none -z-20 absolute left-0 bottom-0 h-64 w-64 opacity-15 transform -translate-x-2/3 text-primary-500`}
`;



export default ({
  subheading = "Transforming Spaces, Building Dreams",
  heading = "Our Services at HB General Contractors",
  description = "At HB General Contractors, we offer a comprehensive range of renovation services designed to breathe new life into your space. Whether it's a commercial setup or a cozy home, our expert team is equipped to handle projects of any size with precision and care. Explore our services below to find how we can make your renovation dreams a reality.",
  faqs = [
    {
      question: "Painting Services",
      answer:
        "Refresh Your World: Dive into a spectrum of colors with our professional painting services. Whether aiming for a bold statement or a subtle ambiance, our painting solutions promise a flawless finish that revives and beautifies your walls."
    },
    {
      question: "Epoxy Flooring",
      answer:
        "Durable Elegance: Elevate your floors with our high-quality epoxy flooring services. Ideal for both homes and businesses, our epoxy solutions offer a blend of durability and style, ensuring your floors look stunning and withstand the test of time."
    },
    {
      question: "Home Renovation",
      answer:
        "Complete Transformation: Turn your house into the home of your dreams with our full-scale renovation services. From updating layouts to refreshing designs, we manage every detail to ensure your renovated space perfectly aligns with your vision and lifestyle."
    },
    {
      question: "Bathroom Renovation",
      answer:
        "Modern Sanctuaries: Create a bathroom that combines functionality with luxury. Our bathroom renovation services focus on crafting spaces that offer comfort and style, utilizing the latest designs and quality materials."
    },
    {
      question: "Basement Renovation",
      answer:
        "Unlock Potential: Transform your basement from an underused area into a vibrant, functional space. Whether you envision a home theater, a playroom, or an extra bedroom, our team can turn your basement ideas into a beautiful reality."
    },
    {
      question: "Kitchen Renovation",
      answer:
        "Heart of the Home: Reinvent your kitchen with our renovation services that blend aesthetics with utility. Our experts work with you to design a kitchen that not only looks beautiful but also enhances your cooking and dining experiences."
    },
    {
      question: "Drywall Installation",
      answer:
        "Seamless Finishes: Achieve perfectly smooth walls and ceilings with our drywall installation services. Whether it's for repairs or new constructions, our skilled technicians ensure a flawless finish that forms the ideal backdrop for your interior decor."
    }
  ]
}) => {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);

  const toggleQuestion = questionIndex => {
    if (activeQuestionIndex === questionIndex) setActiveQuestionIndex(null);
    else setActiveQuestionIndex(questionIndex);
  };

  return (
    <Container>
      <ContentWithPaddingXl>
        <Column>
          <HeaderContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            {description && <Description>{description}</Description>}
          </HeaderContent>
          <FAQSContainer>
            {faqs.map((faq, index) => (
              <FAQ
                key={index}
                onClick={() => {
                  toggleQuestion(index);
                }}
                className="group"
              >
                <Question>
                  <QuestionText>{faq.question}</QuestionText>
                  <QuestionToggleIcon
                    variants={{
                      collapsed: { rotate: 0 },
                      open: { rotate: -180 }
                    }}
                    initial="collapsed"
                    animate={activeQuestionIndex === index ? "open" : "collapsed"}
                    transition={{ duration: 0.02, ease: [0.04, 0.62, 0.23, 0.98] }}
                  >
                    <ChevronDownIcon />
                  </QuestionToggleIcon>
                </Question>
                <Answer
                  variants={{
                    open: { opacity: 1, height: "auto", marginTop: "16px" },
                    collapsed: { opacity: 0, height: 0, marginTop: "0px" }
                  }}
                  initial="collapsed"
                  animate={activeQuestionIndex === index ? "open" : "collapsed"}
                  transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                >
                  {faq.answer}
                </Answer>
              </FAQ>
            ))}
          </FAQSContainer>
        </Column>
      </ContentWithPaddingXl>
      <DecoratorBlob1/>
      <DecoratorBlob2 />
    </Container>
  );
};
